import { useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingDomainsV2QueryKey,
  useHostingDomainsV2,
  useSiteUpdate,
} from '@newfold/huapi-js';
import {
  HostingDomainsV2Params,
  HostingDomainsV2200DomainsItem,
  HostingSitesV2200ItemsItem,
  SitesDomainsV2200DomainsItem,
  SitesInfo200,
  SiteUpdateBody,
} from '@newfold/huapi-js/src/index.schemas';

import useAlerts from '~/components/Alerts/alertsStore';
import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { checkWordPressType, getDocrootsFromSite } from '~/utils/site/helpers';

import { checkDomainDocrootEqualsSiteDocpath } from '../utils/helpers';

export type ChangeSiteURLModalPropOptions = {
  domain?: SitesDomainsV2200DomainsItem | HostingDomainsV2200DomainsItem;
  isRetry?: boolean;
  open?: boolean;
  setOpen: Function;
  siteInfo?: SitesInfo200 | HostingSitesV2200ItemsItem;
};

const ChangeSiteURLModal = ({
  domain = undefined,
  isRetry = false,
  open = false,
  setOpen,
  siteInfo = undefined,
}: PropsWithChildren<ChangeSiteURLModalPropOptions>) => {
  const { t } = useTranslation('domains');
  const [, { generateAlert }] = useAlerts();
  const { siteId: paramsSiteId } = useParams() as { siteId: string };
  const siteIdNum: number = !!siteInfo?.id
    ? Number(siteInfo?.id)
    : Number(paramsSiteId);
  const { id: hostingId } = useAccount();

  const queryClient = useQueryClient();
  const { phoneNumberFormatted } = useBrandInfo();

  const domainName = domain?.domain;
  const descriptionKey = isRetry ? 'retryDescription' : 'description';
  const titleKey = isRetry ? 'retryTitle' : 'title';
  const isWordPress = checkWordPressType({
    type: siteInfo?.type,
    detected_type: siteInfo?.detected_type,
  });
  const siteTypeText = isWordPress ? 'WordPress ' : ''; // Yes the trailing space is intentional

  const sitePath = siteInfo?.path ?? undefined;
  const domainDocrootEqualsSiteDocpath = checkDomainDocrootEqualsSiteDocpath(
    domain,
    siteInfo,
  );
  const newPendingSiteUrl =
    sitePath && !domainDocrootEqualsSiteDocpath
      ? `${domainName}/${sitePath}`
      : domainName;

  const [isPollingDomains, setIsPollingDomains] = useState(false);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const hostingDomainsQueryKey = getHostingDomainsV2QueryKey(hostingId);

  const hostingDomainsV2Params: HostingDomainsV2Params = {
    docroots: getDocrootsFromSite(siteInfo),
  };
  const {
    data: hostingDomainsData,
    refetch: refetchHostingDomainsList,
    isSuccess: hostingDomainsSuccess,
  } = useHostingDomainsV2(hostingId, hostingDomainsV2Params, {
    query: {
      staleTime: 30000,
      enabled: !!hostingId && isPollingDomains,
      refetchInterval: (query) => {
        const queryStateData = query?.state?.data;
        const domains = queryStateData?.data?.domains;
        const domainStatus = domains?.find(
          (d) => d.domain === domainName,
        )?.status;
        const keepPolling = isPollingDomains && domainStatus !== 'active';
        return keepPolling ? 5000 : false;
      },
    },
  });

  // onSuccess - useHostingDomainsV2
  useEffect(() => {
    if (hostingDomainsSuccess && isPollingDomains) {
      const domains = hostingDomainsData?.data?.domains;
      const domainStatus = domains?.find(
        (d) => d.domain === domainName,
      )?.status;
      const siteUrlUpdated = domainStatus === 'active';

      if (siteUrlUpdated) {
        generateAlert({
          description: (
            <Trans
              i18nKey="domains:changeSiteURLModal.successMessage"
              values={{
                url: newPendingSiteUrl,
                siteTypeText,
              }}
              components={{ b: <b /> }}
            />
          ),
          severity: 'success',
          showCloseBtn: true,
        });
        setIsPollingDomains(false);
        handleClose();
        queryClient.invalidateQueries({ queryKey: hostingDomainsQueryKey });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostingDomainsSuccess, hostingDomainsData, isPollingDomains]);

  const { mutate: updateSiteUrl, isPending } = useSiteUpdate({
    mutation: {
      onError: () => {
        generateAlert({
          description: (
            <Trans
              i18nKey="domains:changeSiteURLModal.errorMessage"
              values={{ phone: phoneNumberFormatted }}
              components={{ b: <b /> }}
            />
          ),
          severity: 'error',
          showCloseBtn: true,
        });
        handleClose();
      },
      onSuccess: () => {
        setIsPollingDomains(true);
        refetchHostingDomainsList();
      },
    },
  });

  const handleConfirm = () => {
    let data: SiteUpdateBody = { domain: domainName, skip_fg_checks: true };
    if (sitePath && !domainDocrootEqualsSiteDocpath) {
      data = { ...data, path: sitePath };
    }
    updateSiteUrl({
      siteId: siteIdNum,
      data,
    });
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="change-site-url-modal-title"
      aria-describedby="change-site-url-modal-description"
      data-testid="change-site-url-modal"
    >
      <DialogContent>
        <Stack direction="row" alignItems="center">
          <WarningIcon color="error" />
          <DialogTitle>
            <Trans
              i18nKey={`domains:changeSiteURLModal.${titleKey}`}
              values={{ siteTypeText }}
            />
          </DialogTitle>
        </Stack>
        <Divider />
        <Typography variant="body1" py={3}>
          <Trans
            i18nKey={`domains:changeSiteURLModal.${descriptionKey}`}
            values={{
              url: newPendingSiteUrl,
              siteTypeText,
            }}
            components={{ b: <b /> }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2}>
          <Button
            variant="text"
            onClick={handleClose}
            data-testid="change-site-url-cancel-btn"
          >
            {t('changeSiteURLModal.cancelBtn')}
          </Button>
          <LoadingButtonWrapper
            onClick={handleConfirm}
            loading={isPending || isPollingDomains}
            data-testid="change-site-url-confirm-btn"
          >
            {t('changeSiteURLModal.confirmBtn')}
          </LoadingButtonWrapper>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeSiteURLModal;
